import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, ArrowLeft, ArrowRight, Sparkles, Tag } from "lucide-react";
import { api } from "@/lib/api";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import VisibilityPicker from "@/components/VisibilityPicker";
import UnitsPicker from "@/components/UnitsPicker";
import PhotoUpload from "@/components/PhotoUpload";

const QUESTION_TYPES = [
  { value: "text", label: "Texto" },
  { value: "number", label: "Número" },
  { value: "yes_no", label: "Sim / Não" },
  { value: "temperature", label: "Temperatura" },
  { value: "photo", label: "Foto (com IA obrigatória)" },
  { value: "signature", label: "Assinatura" },
  { value: "qr_code", label: "QR Code" },
  { value: "multiple_choice", label: "Múltipla escolha" },
  { value: "stock_control", label: "Controle de estoque" },
];

const newQuestion = () => ({
  id: crypto.randomUUID(),
  label: "",
  type: "text",
  required: true,
  options: [],
  compliant_option: "",
  min_value: null,
  max_value: null,
  target_value: "",
  help_text: "",
  require_ai_validation: false,
  reference_photo_url: "",
  stock_item_ids: [],
  stock_operation: "out",
});

export default function ChecklistEditor() {
  const navigate = useNavigate();
  const { id: editId } = useParams(); // FASE 19 — edit mode when present
  const [loaded, setLoaded] = useState(!editId);
  const [step, setStep] = useState(1); // 1 = configurações · 2 = perguntas
  const [form, setForm] = useState({
    name: "",
    description: "",
    category: "operacao",
    frequency: "daily",
    weekday: null,
    day_of_month: null,
    time_of_day: "06:00",
    shift: "",
    auto_create_occurrence: true,
    assigned_roles: [],
    assigned_user_ids: [],
    assigned_sector_ids: [],
    unit_ids: [],
    deadline_minutes: null,
    questions: [],
  });
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catDialog, setCatDialog] = useState(false);
  const [catForm, setCatForm] = useState({ key: "", label: "" });
  const [stockItems, setStockItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [sectors, setSectors] = useState([]);

  const loadCategories = async () => {
    try {
      const { data } = await api.get("/checklists/categories");
      setCategories(data);
    } catch (err) {
      console.error("Failed to load checklist categories", err);
    }
  };

  const loadStockItems = async () => {
    try {
      const { data } = await api.get("/sanitary/products");
      setStockItems(data);
    } catch (err) {
      console.error("Failed to load stock products", err);
    }
  };

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) {
      console.error("Failed to load users", err);
    }
  };

  const loadUnits = async () => {
    try {
      const { data } = await api.get("/units");
      setUnits(data);
    } catch (err) {
      console.error("Failed to load units", err);
    }
  };

  useEffect(() => {
    loadCategories();
    loadStockItems();
    loadUsers();
    loadUnits();
    api.get("/sectors").then(({ data }) => setSectors(data)).catch(() => {});
  }, []);

  // FASE 19 — edit mode: load the existing template into the form
  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const { data } = await api.get(`/checklists/templates/${editId}`);
        setForm({
          name: data.name || "",
          description: data.description || "",
          category: data.category || "operacao",
          frequency: data.frequency || "daily",
          weekday: data.weekday ?? null,
          day_of_month: data.day_of_month ?? null,
          time_of_day: data.time_of_day || "06:00",
          shift: data.shift || "",
          auto_create_occurrence: data.auto_create_occurrence !== false,
          assigned_roles: data.assigned_roles || [],
          assigned_user_ids: data.assigned_user_ids || [],
          assigned_sector_ids: data.assigned_sector_ids || [],
          unit_ids: data.unit_ids || [],
          deadline_minutes: data.deadline_minutes ?? null,
          questions: data.questions || [],
        });
        setLoaded(true);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Falha ao carregar checklist");
        navigate("/checklists");
      }
    })();
  }, [editId, navigate]);

  const saveCategory = async () => {
    const key = catForm.key.trim().toLowerCase().replace(/\s+/g, "_");
    if (!key || !catForm.label.trim()) {
      toast.error("Nome e chave são obrigatórios");
      return;
    }
    try {
      await api.post("/checklists/categories", {
        key,
        label: catForm.label.trim(),
      });
      toast.success("Categoria criada");
      setCatDialog(false);
      setCatForm({ key: "", label: "" });
      await loadCategories();
      setForm((f) => ({ ...f, category: key }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao criar categoria");
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateQ = (id, patch) =>
    setForm((f) => ({
      ...f,
      questions: f.questions.map((q) => (q.id === id ? { ...q, ...patch } : q)),
    }));

  const removeQ = (id) =>
    setForm((f) => ({ ...f, questions: f.questions.filter((q) => q.id !== id) }));

  const addQ = () => setForm((f) => ({ ...f, questions: [...f.questions, newQuestion()] }));

  const generateWithAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Descreva a operação que precisa do checklist");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/generate-checklist", {
        prompt: aiPrompt,
        category: form.category,
      });
      // Map AI response into our form structure (preserve existing meta if user already set)
      setForm((f) => ({
        ...f,
        name: data.name || f.name || aiPrompt.slice(0, 60),
        description: data.description || f.description,
        category: data.category || f.category,
        frequency: data.frequency || f.frequency,
        questions: (data.questions || []).map((q) => ({
          id: q.id || crypto.randomUUID(),
          label: q.label || "",
          type: q.type || "text",
          required: q.required ?? true,
          options: q.options || [],
          compliant_option: q.compliant_option || "",
          min_value: q.min_value ?? null,
          max_value: q.max_value ?? null,
          target_value: q.target_value || "",
          help_text: q.help_text || "",
        })),
      }));
      toast.success(`IA gerou ${data.questions?.length || 0} perguntas`);
      setAiOpen(false);
      setAiPrompt("");
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha na IA");
    } finally {
      setAiLoading(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Nome do template é obrigatório");
      return;
    }
    if (form.questions.length === 0) {
      toast.error("Adicione pelo menos uma pergunta");
      return;
    }
    try {
      const payload = {
        ...form,
        deadline_minutes:
          form.deadline_minutes === "" || form.deadline_minutes === null
            ? null
            : Number(form.deadline_minutes),
        questions: form.questions.map((q) => ({
          ...q,
          min_value: q.min_value === "" ? null : q.min_value,
          max_value: q.max_value === "" ? null : q.max_value,
        })),
      };
      if (editId) {
        await api.patch(`/checklists/templates/${editId}`, payload);
        toast.success("Template atualizado!");
      } else {
        await api.post("/checklists/templates", payload);
        toast.success("Template criado!");
      }
      navigate("/checklists");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    }
  };

  const goStep2 = () => {
    if (!form.name.trim()) {
      toast.error("Nome do template é obrigatório");
      return;
    }
    setStep(2);
  };

  if (!loaded) {
    return (
      <Layout title="Carregando..." subtitle="">
        <div className="text-stone-500 font-body">Aguarde...</div>
      </Layout>
    );
  }

  const stepPillClass = (n) =>
    `inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-body font-semibold transition-colors ${
      step === n
        ? "bg-emerald-800 text-white"
        : "bg-stone-100 text-stone-600 hover:bg-stone-200"
    }`;

  return (
    <Layout
      title={editId ? "Editar Checklist" : "Novo Checklist"}
      subtitle={editId ? "Ajuste perguntas, prazos e visibilidade" : "Defina perguntas tipadas, parâmetros de conformidade e regras"}
    >
      <div data-testid="checklist-editor" className="text-sm text-stone-500 font-body">
        O editor está aberto em uma janela. Ao fechar, você volta para a lista de checklists.
      </div>

      {/* AI generator (diálogo separado) */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Gerar checklist com IA</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-stone-600 font-body">
              Descreva sua operação em PT-BR. A IA criará perguntas relevantes
              para APPCC, segurança alimentar e ANVISA.
            </p>
            <Textarea
              rows={5}
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Ex: Cozinha hospitalar com câmaras frias para carnes e laticínios, dieta enteral, recebimento diário às 7h..."
              className="bg-white"
              data-testid="ai-prompt"
            />
          </div>
          <DialogFooter>
            <Button
              onClick={generateWithAI}
              disabled={aiLoading}
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              data-testid="ai-generate"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {aiLoading ? "Gerando…" : "Gerar perguntas"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wizard em popup: etapa 1 = configurações, etapa 2 = perguntas */}
      <Dialog
        open
        onOpenChange={(o) => {
          if (!o) navigate("/checklists");
        }}
      >
        <DialogContent
          className="max-w-4xl w-[96vw] max-h-[92vh] p-0 gap-0 flex flex-col overflow-hidden"
          onInteractOutside={(e) => e.preventDefault()}
          data-testid="checklist-editor-dialog"
        >
          <DialogHeader className="px-6 pt-5 pb-4 border-b border-stone-200 space-y-3">
            <DialogTitle className="font-display">
              {editId ? "Editar Checklist" : "Novo Checklist"}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setStep(1)} className={stepPillClass(1)} data-testid="editor-step-1">
                1 · Configurações
              </button>
              <div className="h-px w-6 bg-stone-300" />
              <button type="button" onClick={goStep2} className={stepPillClass(2)} data-testid="editor-step-2">
                2 · Perguntas
                {form.questions.length > 0 && (
                  <span className="ml-1 rounded-full bg-white/20 px-1.5 text-[10px]">
                    {form.questions.length}
                  </span>
                )}
              </button>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5 bg-stone-50/50">
        {step === 1 && (
        <div className="space-y-4" data-testid="editor-config-step">
          <div>
            <Label className="font-body text-sm">Nome *</Label>
            <Input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Ex: Checklist de abertura"
              className="mt-1 bg-white"
              data-testid="editor-name"
            />
          </div>
          <div>
            <Label className="font-body text-sm">Descrição</Label>
            <Textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={3}
              className="mt-1 bg-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between">
                <Label className="font-body text-sm">Categoria</Label>
                <Dialog open={catDialog} onOpenChange={setCatDialog}>
                  <DialogTrigger asChild>
                    <button
                      type="button"
                      className="text-[11px] font-body text-emerald-800 hover:text-emerald-900 hover:underline inline-flex items-center gap-1"
                      data-testid="editor-new-category"
                    >
                      <Tag className="h-3 w-3" /> Nova categoria
                    </button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-display">Nova categoria de checklist</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <Label>Nome exibido *</Label>
                        <Input
                          value={catForm.label}
                          onChange={(e) =>
                            setCatForm({ ...catForm, label: e.target.value })
                          }
                          placeholder="Ex: Higienização"
                          className="mt-1 bg-white"
                          data-testid="editor-cat-label"
                          autoFocus
                        />
                      </div>
                      <div>
                        <Label>Chave interna *</Label>
                        <Input
                          value={catForm.key}
                          onChange={(e) =>
                            setCatForm({
                              ...catForm,
                              key: e.target.value.toLowerCase().replace(/\s+/g, "_"),
                            })
                          }
                          placeholder="higienizacao"
                          className="mt-1 bg-white font-mono text-sm"
                          data-testid="editor-cat-key"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        onClick={saveCategory}
                        className="bg-emerald-800 hover:bg-emerald-900 text-white"
                        data-testid="editor-cat-submit"
                      >
                        Criar categoria
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1 bg-white" data-testid="editor-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.length === 0 && (
                    <>
                      <SelectItem value="operacao">Operação</SelectItem>
                      <SelectItem value="abertura">Abertura</SelectItem>
                      <SelectItem value="fechamento">Fechamento</SelectItem>
                      <SelectItem value="appcc">APPCC</SelectItem>
                      <SelectItem value="limpeza">Limpeza</SelectItem>
                      <SelectItem value="recebimento">Recebimento</SelectItem>
                    </>
                  )}
                  {categories.map((c) => (
                    <SelectItem key={c.key} value={c.key}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-body text-sm">Frequência</Label>
              <Select
                value={form.frequency}
                onValueChange={(v) => {
                  // Reset recurrence fields when frequency changes
                  const patch = { frequency: v };
                  if (v !== "weekly") patch.weekday = null;
                  if (v !== "monthly") patch.day_of_month = null;
                  if (v === "weekly" && form.weekday == null) patch.weekday = 1; // Tue default (Portuguese "Segunda"=0)
                  if (v === "monthly" && form.day_of_month == null) patch.day_of_month = 1;
                  setForm((f) => ({ ...f, ...patch }));
                }}
              >
                <SelectTrigger className="mt-1 bg-white" data-testid="editor-frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Diário</SelectItem>
                  <SelectItem value="weekly">Semanal</SelectItem>
                  <SelectItem value="monthly">Mensal</SelectItem>
                  <SelectItem value="shift">Por turno</SelectItem>
                  <SelectItem value="custom">Customizado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.frequency === "weekly" && (
            <div>
              <Label className="font-body text-sm">Dia da semana</Label>
              <Select
                value={String(form.weekday ?? 0)}
                onValueChange={(v) => set("weekday", Number(v))}
              >
                <SelectTrigger className="mt-1 bg-white" data-testid="editor-weekday">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Segunda-feira</SelectItem>
                  <SelectItem value="1">Terça-feira</SelectItem>
                  <SelectItem value="2">Quarta-feira</SelectItem>
                  <SelectItem value="3">Quinta-feira</SelectItem>
                  <SelectItem value="4">Sexta-feira</SelectItem>
                  <SelectItem value="5">Sábado</SelectItem>
                  <SelectItem value="6">Domingo</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-stone-500 font-body mt-1">
                O checklist se repete toda semana neste dia.
              </p>
            </div>
          )}
          {form.frequency === "monthly" && (
            <div>
              <Label className="font-body text-sm">Dia do mês</Label>
              <Select
                value={String(form.day_of_month ?? 1)}
                onValueChange={(v) => set("day_of_month", Number(v))}
              >
                <SelectTrigger className="mt-1 bg-white" data-testid="editor-day-of-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      Dia {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-stone-500 font-body mt-1">
                O checklist se repete todo mês neste dia (limitado a 28 para meses curtos).
              </p>
            </div>
          )}
          {["daily", "weekly", "monthly"].includes(form.frequency) && (
            <div>
              <Label className="font-body text-sm">Horário de execução</Label>
              <Input
                type="time"
                value={form.time_of_day || "06:00"}
                onChange={(e) => set("time_of_day", e.target.value)}
                className="mt-1 bg-white"
                data-testid="editor-time-of-day"
              />
              <p className="text-[11px] text-stone-500 font-body mt-1">
                O checklist entra nas pendências automaticamente neste horário
                (horário de Brasília). O agendamento é criado e mantido sozinho.
              </p>
            </div>
          )}
          <div>
            <Label className="font-body text-sm">Prazo para execução (minutos)</Label>
            <Input
              type="number"
              min="0"
              value={form.deadline_minutes ?? ""}
              onChange={(e) =>
                set("deadline_minutes", e.target.value === "" ? null : Number(e.target.value))
              }
              placeholder="Ex: 60"
              className="mt-1 bg-white"
              data-testid="editor-deadline"
            />
            <p className="text-[11px] text-stone-500 font-body mt-1">
              Tempo (em minutos) para concluir após o checklist entrar nas pendências.
              Impacta a qualificação de pontualidade. Vazio = até o fim do dia.
            </p>
          </div>
          <UnitsPicker
            value={form.unit_ids || []}
            onChange={(v) => set("unit_ids", v)}
            units={units}
            label="Unidades"
            description="Para quais unidades este checklist pertence."
            testPrefix="editor-units"
          />

          <VisibilityPicker form={form} setForm={setForm} users={users} sectors={sectors} />
          <label className="flex items-center justify-between border border-stone-200 rounded-md px-3 py-2 text-sm font-body bg-white">
            <div>
              <div className="font-semibold">Auto-criar ocorrência</div>
              <div className="text-xs text-stone-500">Ao detectar não conformidade</div>
            </div>
            <Switch
              checked={form.auto_create_occurrence}
              onCheckedChange={(v) => set("auto_create_occurrence", v)}
              data-testid="editor-auto-occurrence"
            />
          </label>
        </div>
        )}

        {step === 2 && (
        <div className="space-y-3" data-testid="editor-questions-step">
          {form.questions.length === 0 && (
            <Card className="p-10 text-center text-stone-500 font-body rounded-xl border-stone-200 bg-white">
              <Plus className="h-6 w-6 mx-auto mb-2 text-stone-400" />
              Adicione perguntas ao seu checklist
            </Card>
          )}

          {form.questions.map((q, idx) => (
            <Card key={q.id} className="p-5 rounded-xl border-stone-200 bg-white" data-testid={`question-${idx}`}>
              <div className="flex items-start gap-3">
                <div className="h-7 w-7 rounded-md bg-emerald-100 text-emerald-800 flex items-center justify-center font-display font-bold text-sm">
                  {idx + 1}
                </div>
                <div className="flex-1 grid gap-3">
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <Label className="font-body text-xs uppercase tracking-wider text-stone-500">Pergunta</Label>
                      <Input
                        value={q.label}
                        onChange={(e) => updateQ(q.id, { label: e.target.value })}
                        placeholder="Ex: Temperatura da câmara fria 1"
                        className="mt-1 bg-white"
                        data-testid={`question-label-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="font-body text-xs uppercase tracking-wider text-stone-500">Tipo</Label>
                      <Select
                        value={q.type}
                        onValueChange={(v) => {
                          // Sensible defaults per type
                          const patch = { type: v };
                          if (v === "photo") patch.require_ai_validation = true;
                          updateQ(q.id, patch);
                        }}
                      >
                        <SelectTrigger className="mt-1 bg-white" data-testid={`question-type-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {QUESTION_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {(q.type === "number" || q.type === "temperature") && (
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="font-body text-xs uppercase tracking-wider text-stone-500">Mínimo</Label>
                        <Input
                          type="number"
                          value={q.min_value ?? ""}
                          onChange={(e) => updateQ(q.id, { min_value: e.target.value === "" ? null : Number(e.target.value) })}
                          className="mt-1 bg-white"
                          placeholder={q.type === "temperature" ? "-5" : ""}
                        />
                      </div>
                      <div>
                        <Label className="font-body text-xs uppercase tracking-wider text-stone-500">Máximo</Label>
                        <Input
                          type="number"
                          value={q.max_value ?? ""}
                          onChange={(e) => updateQ(q.id, { max_value: e.target.value === "" ? null : Number(e.target.value) })}
                          className="mt-1 bg-white"
                          placeholder={q.type === "temperature" ? "5" : ""}
                        />
                      </div>
                    </div>
                  )}

                  {q.type === "yes_no" && (
                    <div>
                      <Label className="font-body text-xs uppercase tracking-wider text-stone-500">Resposta conforme</Label>
                      <Select value={q.compliant_option || "yes"} onValueChange={(v) => updateQ(q.id, { compliant_option: v })}>
                        <SelectTrigger className="mt-1 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Sim é conforme</SelectItem>
                          <SelectItem value="no">Não é conforme</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {q.type === "multiple_choice" && (
                    <>
                      <div>
                        <Label className="font-body text-xs uppercase tracking-wider text-stone-500">
                          Opções (separe por vírgula)
                        </Label>
                        <Input
                          value={(q.options || []).join(", ")}
                          onChange={(e) =>
                            updateQ(q.id, {
                              options: e.target.value
                                .split(",")
                                .map((s) => s.trim())
                                .filter(Boolean),
                            })
                          }
                          placeholder="Bom, Razoável, Ruim"
                          className="mt-1 bg-white"
                        />
                      </div>
                      <div>
                        <Label className="font-body text-xs uppercase tracking-wider text-stone-500">
                          Resposta conforme
                        </Label>
                        <Input
                          value={q.compliant_option || ""}
                          onChange={(e) => updateQ(q.id, { compliant_option: e.target.value })}
                          placeholder="Ex: Bom"
                          className="mt-1 bg-white"
                        />
                      </div>
                    </>
                  )}

                  {q.type === "stock_control" && (
                    <div className="space-y-3">
                      <div>
                        <Label className="font-body text-xs uppercase tracking-wider text-stone-500">
                          Operação
                        </Label>
                        <div className="mt-1 grid grid-cols-2 gap-2">
                          <label
                            className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                              (q.stock_operation || "out") === "out"
                                ? "border-rose-300 bg-rose-50"
                                : "border-stone-200 bg-white hover:border-stone-300"
                            }`}
                          >
                            <input
                              type="radio"
                              className="mt-1 accent-rose-600"
                              checked={(q.stock_operation || "out") === "out"}
                              onChange={() =>
                                updateQ(q.id, { stock_operation: "out" })
                              }
                              data-testid={`question-op-out-${idx}`}
                            />
                            <div>
                              <div className="font-semibold text-sm text-rose-800">
                                Baixar estoque
                              </div>
                              <div className="text-[11px] text-stone-600 font-body">
                                Subtrai a quantidade informada (consumo, perda).
                              </div>
                            </div>
                          </label>
                          <label
                            className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                              q.stock_operation === "in"
                                ? "border-emerald-300 bg-emerald-50"
                                : "border-stone-200 bg-white hover:border-stone-300"
                            }`}
                          >
                            <input
                              type="radio"
                              className="mt-1 accent-emerald-700"
                              checked={q.stock_operation === "in"}
                              onChange={() =>
                                updateQ(q.id, { stock_operation: "in" })
                              }
                              data-testid={`question-op-in-${idx}`}
                            />
                            <div>
                              <div className="font-semibold text-sm text-emerald-800">
                                Adicionar estoque
                              </div>
                              <div className="text-[11px] text-stone-600 font-body">
                                Soma ao saldo (recebimento, contagem).
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                      <div>
                        <Label className="font-body text-xs uppercase tracking-wider text-stone-500">
                          Produtos de estoque cobertos por esta pergunta
                        </Label>
                        <div className="mt-1 border border-stone-200 rounded-md bg-white max-h-48 overflow-y-auto p-2 space-y-1">
                          {stockItems.length === 0 && (
                            <div className="text-xs text-stone-500 font-body italic py-2">
                              Cadastre produtos em Estoque antes de usar este tipo.
                            </div>
                          )}
                          {stockItems.map((it) => {
                            const checked = (q.stock_item_ids || []).includes(it.id);
                            return (
                              <label
                                key={it.id}
                                className="flex items-center gap-2 text-sm font-body cursor-pointer hover:bg-stone-50 px-1.5 py-0.5 rounded"
                                data-testid={`stock-product-option-${it.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    const cur = new Set(q.stock_item_ids || []);
                                    if (e.target.checked) cur.add(it.id);
                                    else cur.delete(it.id);
                                    updateQ(q.id, { stock_item_ids: Array.from(cur) });
                                  }}
                                  className="accent-emerald-700"
                                />
                                <span className="flex-1 truncate">{it.name}</span>
                                <span className="text-xs text-stone-500">
                                  {it.quantity} {it.unit}
                                  {it.lots_count > 1 ? ` · ${it.lots_count} lotes` : ""}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                        <p className="text-[11px] text-stone-500 font-body mt-1">
                          {q.stock_operation === "in"
                            ? "O executor informa a quantidade recebida. O saldo é somado após aprovação do revisor."
                            : "O executor informa a quantidade consumida (ou lê a etiqueta QR do lote). A baixa segue PVPS após aprovação do revisor; etiqueta fora da ordem gera ocorrência."}
                        </p>
                      </div>
                    </div>
                  )}

                  {q.type === "photo" && (
                    <label className="flex items-center gap-2 text-sm font-body border border-emerald-200 bg-emerald-50/50 rounded-md px-3 py-2">
                      <Switch
                        checked={q.require_ai_validation !== false}
                        onCheckedChange={(v) =>
                          updateQ(q.id, { require_ai_validation: v })
                        }
                        data-testid={`question-ai-required-${idx}`}
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-emerald-900">
                          Exigir validação por IA
                        </div>
                        <div className="text-xs text-emerald-800/80">
                          Bloqueia envio até o executor rodar &quot;Validar com IA&quot; na foto.
                        </div>
                      </div>
                    </label>
                  )}

                  {/* FASE 20 — reference photo for AI comparison */}
                  {q.type === "photo" && (
                    <div className="border border-stone-200 rounded-md px-3 py-2 bg-stone-50/40" data-testid={`question-reference-${idx}`}>
                      <div className="font-body text-sm font-semibold text-stone-800">
                        Foto de referência (opcional)
                      </div>
                      <p className="text-[11px] text-stone-500 font-body mt-0.5 mb-2">
                        Envie a foto do padrão correto. A IA comparará a foto do
                        operador com esta referência na avaliação.
                      </p>
                      <PhotoUpload
                        value={q.reference_photo_url || ""}
                        onChange={(url) => updateQ(q.id, { reference_photo_url: url })}
                        allowGallery
                        showAiValidate={false}
                        testId={`question-reference-photo-${idx}`}
                      />
                    </div>
                  )}

                  {(q.type === "text" || q.type === "qr_code") && (
                    <div>
                      <Label className="font-body text-xs uppercase tracking-wider text-stone-500">
                        Valor esperado (opcional)
                      </Label>
                      <Input
                        value={q.target_value || ""}
                        onChange={(e) => updateQ(q.id, { target_value: e.target.value })}
                        className="mt-1 bg-white"
                      />
                    </div>
                  )}

                  <div>
                    <Label className="font-body text-xs uppercase tracking-wider text-stone-500">Ajuda (opcional)</Label>
                    <Input
                      value={q.help_text}
                      onChange={(e) => updateQ(q.id, { help_text: e.target.value })}
                      className="mt-1 bg-white"
                    />
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <label className="flex items-center gap-2 text-sm font-body">
                      <Switch
                        checked={q.required}
                        onCheckedChange={(v) => updateQ(q.id, { required: v })}
                        data-testid={`question-required-${idx}`}
                      />
                      Resposta obrigatória
                    </label>
                    <Badge variant="outline" className="border-stone-300">
                      {QUESTION_TYPES.find((t) => t.value === q.type)?.label}
                    </Badge>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeQ(q.id)}
                  data-testid={`question-remove-${idx}`}
                >
                  <Trash2 className="h-4 w-4 text-rose-600" />
                </Button>
              </div>
            </Card>
          ))}

          <Button
            onClick={addQ}
            variant="outline"
            className="w-full h-12 border-dashed border-stone-300 font-body bg-white"
            data-testid="editor-add-question"
          >
            <Plus className="h-4 w-4 mr-2" /> Adicionar pergunta
          </Button>
        </div>
        )}
          </div>

          <DialogFooter className="px-6 py-4 border-t border-stone-200 bg-white flex-row items-center justify-between sm:justify-between gap-2">
            {step === 1 ? (
              <>
                <Button variant="outline" onClick={() => navigate("/checklists")} data-testid="editor-cancel">
                  Cancelar
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    className="border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                    onClick={() => setAiOpen(true)}
                    data-testid="editor-ai"
                  >
                    <Sparkles className="h-4 w-4 mr-2" /> Gerar com IA
                  </Button>
                  <Button
                    onClick={goStep2}
                    className="bg-emerald-800 hover:bg-emerald-900 text-white"
                    data-testid="editor-step-next"
                  >
                    Avançar para perguntas <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setStep(1)} data-testid="editor-step-back">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    className="border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                    onClick={() => setAiOpen(true)}
                    data-testid="editor-ai-step2"
                  >
                    <Sparkles className="h-4 w-4 mr-2" /> Gerar com IA
                  </Button>
                  <Button
                    onClick={save}
                    className="bg-emerald-800 hover:bg-emerald-900 text-white"
                    data-testid="editor-save"
                  >
                    Salvar template
                  </Button>
                </div>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

